import type { IFile } from '@/api/nodes/mutations/directory/create-directory-mutation'
import type { IFileUploadItemState } from '@/features/drive/functionalities/upload/utils/directory-file-upload-queue'

/** A successful confirm request may omit individual files that could not be processed. */
export function reconcileConfirmedFileUploads<TNode extends { file?: Pick<IFile, 'uuid'> | null }>(
    uploaded: { s3Path: string; item: Pick<IFileUploadItemState, 'id'> }[],
    nodes: TNode[]
) {
    // Upload URLs use S3Service.buildS3Path: files/<uuid>/original.<ext>.
    // FileCreateService persists that UUID; transformed paths may point at another rendition.
    const uploads = uploaded.map((entry) => ({ ...entry, fileUuid: entry.s3Path.split('/').at(-2) }))
    const expectedUuids = new Set(uploads.map((entry) => entry.fileUuid))
    const confirmedNodes = nodes.filter((node) => node.file && expectedUuids.has(node.file.uuid))
    const confirmedUuids = new Set(confirmedNodes.map((node) => node.file?.uuid))

    return {
        confirmedNodes,
        outcomes: uploads.map(({ item, fileUuid }) =>
            fileUuid && confirmedUuids.has(fileUuid)
                ? { id: item.id, status: 'uploaded' as const, error: undefined }
                : { id: item.id, status: 'failed' as const, error: 'Could not process this file. Please try again.' }
        ),
    }
}
